<script lang="ts">
  import { inTelegram } from '../lib/telegram';
  import DropZone from './DropZone.svelte';
  import Icon, { type IconName } from './Icon.svelte';

  let {
    busy,
    error,
    onFile,
    onSample,
    onProfile,
    onImport,
    custom,
    fromLink,
    profileName,
  }: {
    busy: boolean;
    error: string | null;
    onFile: (f: File) => void;
    onSample: () => void;
    onProfile: () => void;
    onImport: () => void;
    custom: boolean;
    fromLink: boolean;
    profileName: string;
  } = $props();

  const FEATURES: Array<{ icon: IconName; title: string; text: string }> = [
    {
      icon: 'wifi-off',
      title: 'Файл остаётся у вас',
      text: inTelegram()
        ? 'Документ проверяется и исправляется на вашем устройстве. Готовый файл приходит в чат с ботом, мы его не храним.'
        : 'Проверка работает прямо в браузере — можно даже отключить интернет. Никаких загрузок, аккаунтов и хранения файлов.',
    },
    {
      icon: 'edit',
      title: 'Исправления прямо в Word',
      text: `${inTelegram() ? 'Получите' : 'Скачайте'} свой же .docx с правками в режиме рецензирования и примите их одним кликом. Остальное — в комментариях на полях.`,
    },
    {
      icon: 'sliders',
      title: 'Под вашу методичку',
      text: 'ГОСТ 7.32 для отчётов или ЕСКД для пояснительных записок, правое поле 10 мм, кегль 14 — настройте требования и отправьте ссылку одногруппникам.',
    },
  ];
</script>

<section class="hero">
  <p class="eyebrow">
    <span class="pulse" aria-hidden="true"></span><span>ГОСТ 7.32 и ГОСТ Р 2.105 · курсовые, ВКР, отчёты</span>
  </p>
  <h1>Проверка оформления по ГОСТ <span class="nowrap">{inTelegram() ? 'прямо в Telegram' : 'прямо в браузере'}</span></h1>
  <p class="lead">
    Поля, шрифты, заголовки, подписи рисунков и список литературы — около 60 проверок за пару секунд. Документ не загружается
    на сервер: всё считается на вашем устройстве.
  </p>
  <div class="drop">
    <DropZone {busy} {error} {onFile} {onSample} />
  </div>
  <p class="import-line">
    <Icon name="sparkle" size={16} /><span>Текст от нейросети?</span>
    <button class="link" onclick={onImport}>Соберите из него документ по ГОСТу</button>
  </p>
  <p class="profile-line">
    Требования: <strong>{profileName}</strong>{#if custom}<span class="custom">+ настройки {fromLink ? 'из ссылки' : 'вашей методички'}</span>{/if}.
    <button class="link" onclick={onProfile}>Настроить под методичку</button>
  </p>
</section>

<section class="features" aria-label="Преимущества">
  {#each FEATURES as f (f.title)}
    <article class="feature">
      <div class="f-icon"><Icon name={f.icon} size={22} /></div>
      <h3>{f.title}</h3>
      <p>{f.text}</p>
    </article>
  {/each}
</section>

<style>
  .hero {
    max-width: 760px;
    margin: 0 auto;
    padding: 64px 24px 24px;
    text-align: center;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--surface);
    color: var(--text-2);
    font-size: 13px;
    font-weight: 520;
  }

  .pulse {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--ok);
    box-shadow: 0 0 0 3px var(--ok-soft);
  }

  h1 {
    margin-top: 20px;
    font-size: clamp(34px, 5.4vw, 54px);
    font-weight: 720;
    letter-spacing: -0.035em;
  }

  .nowrap {
    white-space: nowrap;
    color: var(--accent);
  }

  .lead {
    max-width: 620px;
    margin: 18px auto 0;
    color: var(--text-2);
    font-size: 17px;
    line-height: 1.55;
  }

  .drop {
    margin-top: 32px;
  }

  .import-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 16px;
    padding: 10px 14px;
    border-radius: 12px;
    background: var(--accent-soft);
    color: var(--text-2);
    font-size: 14.5px;
  }

  .import-line :global(svg) {
    color: var(--accent);
  }

  .profile-line {
    margin-top: 18px;
    color: var(--text-2);
    font-size: 14px;
  }

  .custom {
    margin-left: 0.3em;
    color: var(--accent);
  }

  .features {
    max-width: 1080px;
    margin: 40px auto 0;
    padding: 0 24px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }

  .feature {
    padding: 22px;
    border-radius: var(--radius);
    background: var(--surface);
    border: 1px solid var(--border);
  }

  .f-icon {
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    border-radius: 12px;
    background: var(--accent-soft);
    color: var(--accent);
  }

  .feature h3 {
    margin-top: 14px;
    font-size: 17px;
    font-weight: 640;
  }

  .feature p {
    margin-top: 6px;
    color: var(--text-2);
    font-size: 14.5px;
  }

  @media (max-width: 900px) {
    .features {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 620px) {
    .hero {
      padding: 40px 16px 16px;
    }

    .features {
      padding: 0 16px;
      grid-template-columns: 1fr;
    }
  }
</style>
